import { z } from 'zod';
import { subscriptionBillingCadenceSchema } from './subscriptionBillingCadence.js';
import { subscriptionPaymentMethodSchema } from './subscriptionPaymentMethod.js';
import { subscriptionPredecessorSummarySchema } from './subscriptionPredecessor.js';
import { subscriptionSaleTypeSchema } from './subscriptionSaleType.js';
import { termsAcceptanceRecordSchema } from './termsAcceptance.js';
export const terminationReasonSchema = z.enum([
    'ScheduledCancellation',
    // The term never began, so Stripe expired it instead of collecting the first payment.
    'FirstPaymentNeverCollected',
    'EndedByStripe',
    // Stopped from BackOps before its commitment ended.
    'EndedByOps',
]);
export const TerminationReason = terminationReasonSchema.enum;
export const subscriptionChangeTypeSchema = z.enum([
    'Activated',
    'Prolongation',
    // Both the scheduled and the actual change are recorded.
    'PlanChangeScheduled',
    'PlanChanged',
    'PlanChangeRevoked',
    'SalesAssigneeChange',
    'TelesalesAssigneeChange',
    'CancellationScheduled',
    'CancellationRevoked',
    'Terminated',
    // Stripe has no dedicated event for this; it arrives as customer.subscription.updated with the old id in
    // previous_attributes.
    'PaymentMethodChanged',
    // Email, phone or billing address, which the partner can edit in the Stripe portal.
    'BillingDetailsChanged',
    // The subscription holds only the latest acceptance, so earlier ones live here.
    'TermsAccepted',
    'ValidationRejected',
    'FreeTimeScheduled',
    'FreeTimeRevoked',
    'FreeTimeGranted',
    // The sold start date or the free days that came with it were renegotiated before collection began.
    'ScheduleChanged',
    // The sale type or the row the subscription is recorded as continuing from was corrected. Tracking only.
    'SaleProvenanceChanged',
    'CreditsApplied',
    'Migration',
    'CheckoutLinkSent',
    // The automated nudge for a checkout the partner has not completed. Not a resend: the schedule stays anchored on
    // the first CheckoutLinkSent.
    'CheckoutReminderSent',
    // The reminder that goes out before a term prolongs, or before a migrated subscription's covered window ends.
    'ProlongationReminderSent',
    // The mail sent when validation passed but the commitment term has not started yet.
    'ValidationConfirmationSent',
    'PaymentMethodValidated',
    'PaymentSuccessful',
    'PaymentFailed',
    // A dunning retry. Stripe emits nothing when it sends its own recovery mail, so this reports the retry itself.
    'PaymentRecoveryAttempt',
    // Collection has begun failing and the retry ladder is running.
    'PaymentRecoveryStarted',
    'PaymentRecoveryExhausted',
    'PaymentActionRequired',
    // The one collection failure no retry can fix, so it is told apart from an ordinary decline.
    'PaymentMethodUnusable',
    'RefundIssued',
    'CreditNoteIssued',
    'DisputeOpened',
    'DisputeClosed',
    'InvoiceVoided',
    'InvoiceUncollectible',
    'BexioInvoiceSent',
    'BexioInvoiceOverdue',
    'InvoiceGenerationFailed',
    'PaymentReceived',
]);
export const SubscriptionChangeType = subscriptionChangeTypeSchema.enum;
// Every amount is net of MWST and travels as a string. Every timestamp is an ISO string.
export const committedPeriodDataSchema = z.object({
    commitmentSequence: z.number().int(),
    commitmentStartedAt: z.iso.datetime(),
    commitmentEndsAt: z.iso.datetime(),
    commitmentLengthInMonths: z.number().int(),
    billingCadence: subscriptionBillingCadenceSchema,
    pricePerMonth: z.string(),
    // What one invoice asks for. Without it an upfront entry reads as a monthly price and implies a total the partner
    // was never charged.
    amountPerCharge: z.string(),
});
export const activatedDataSchema = committedPeriodDataSchema.extend({ planUuid: z.uuid() });
export const prolongationDataSchema = committedPeriodDataSchema;
export const planChangeDataSchema = z.object({
    previousPlanUuid: z.uuid(),
    newPlanUuid: z.uuid(),
    previousPricePerMonth: z.string(),
    newPricePerMonth: z.string(),
    previousUpfrontPrice: z.string().nullable().optional(),
    previousCommitmentLengthInMonths: z.number().int().optional(),
    newCommitmentLengthInMonths: z.number().int(),
    previousBillingCadence: subscriptionBillingCadenceSchema,
    newBillingCadence: subscriptionBillingCadenceSchema,
    previousAmountPerCharge: z.string(),
    newAmountPerCharge: z.string(),
});
export const revokedChangeDataSchema = z.object({ revokedEventUuid: z.uuid() });
export const terminatedDataSchema = z.object({ reason: terminationReasonSchema, endedAt: z.iso.datetime() });
export const assigneeChangeDataSchema = z.object({
    previousAssigneeId: z.number().int().nullable(),
    newAssigneeId: z.number().int().nullable(),
    // Snapshotted the same way changeAuthor on the entry is.
    previousAssignee: z.string().nullable(),
    newAssignee: z.string().nullable(),
});
export const cancellationScheduledDataSchema = z.object({ cancelEffectiveAt: z.iso.datetime() });
export const paymentMethodChangedDataSchema = z.object({
    previousStripePaymentMethodId: z.string().nullable(),
    newStripePaymentMethodId: z.string(),
    previousPaymentMethod: subscriptionPaymentMethodSchema.optional(),
    paymentMethod: subscriptionPaymentMethodSchema.optional(),
});
// Each side of every field, the way ScheduleChanged does it, so the renderer can name only what actually moved.
export const billingDetailsChangedDataSchema = z.object({
    previousEmail: z.string().nullable(),
    newEmail: z.string().nullable(),
    previousPhone: z.string().nullable(),
    newPhone: z.string().nullable(),
    previousAddress: z.string().nullable(),
    newAddress: z.string().nullable(),
});
export const termsAcceptedDataSchema = z.object({
    termsVersion: z.string(),
    acceptance: termsAcceptanceRecordSchema.optional(),
});
export const validationRejectedDataSchema = z.object({ ticketId: z.number().int() });
export const freeTimeScheduledDataSchema = z.object({
    days: z.number().int(),
    coveredFrom: z.iso.datetime(),
    coveredUntil: z.iso.datetime(),
});
// Points back at the entry that booked the window, so an opened grant can be told from one still waiting.
export const freeTimeGrantedDataSchema = freeTimeScheduledDataSchema.extend({
    scheduledEventUuid: z.uuid().optional(),
});
export const creditsAppliedDataSchema = z.object({
    creditsConsumed: z.string(),
    conversionRate: z.string(),
    coveredFrom: z.iso.datetime(),
    coveredUntil: z.iso.datetime(),
    contractIds: z.string(),
});
export const migrationDataSchema = z.object({ pcmContractId: z.number().int() });
export const checkoutLinkSentDataSchema = z.object({
    sentTo: z.email(),
    validUntil: z.iso.datetime(),
    supersededPreviousLink: z.boolean(),
});
export const MAX_CHECKOUT_REMINDERS = 2;
export const checkoutReminderSentDataSchema = z.object({
    // Counts the reminders for this checkout, starting at 1.
    reminder: z.number().int().min(1).max(MAX_CHECKOUT_REMINDERS),
    sentTo: z.email(),
});
export const prolongationReminderReasonSchema = z.enum(['Prolongation', 'CreditsCoverageEnd']);
export const ProlongationReminderReason = prolongationReminderReasonSchema.enum;
export const prolongationReminderSentDataSchema = z.object({
    reason: prolongationReminderReasonSchema,
    renewalDate: z.iso.datetime(),
    sentTo: z.email(),
});
export const validationConfirmationSentDataSchema = z.object({
    paymentMethod: subscriptionPaymentMethodSchema,
    // Set only when converted credits already cover the start, which is the branch of copy the partner was shown.
    creditsCoveredStartDate: z.iso.datetime().nullable(),
});
export const scheduleChangedDataSchema = z.object({
    previousAgreedStartAt: z.iso.datetime().nullable(),
    newAgreedStartAt: z.iso.datetime().nullable(),
    previousInitialFreeDays: z.number().int(),
    newInitialFreeDays: z.number().int(),
});
export const saleProvenanceChangedDataSchema = z.object({
    previousSaleType: subscriptionSaleTypeSchema,
    newSaleType: subscriptionSaleTypeSchema,
    previousPredecessor: subscriptionPredecessorSummarySchema.nullable(),
    newPredecessor: subscriptionPredecessorSummarySchema.nullable(),
});
export const paymentMethodValidatedDataSchema = z.object({
    stripePaymentMethodId: z.string(),
    paymentMethod: subscriptionPaymentMethodSchema,
    stripeMandateId: z.string().nullable(),
});
// Gross, unlike every other amount here, because it is the money that moved rather than what was agreed.
const chargeActivityDataSchema = z.object({ chargeUuid: z.uuid(), amount: z.string() });
export const paymentSettledDataSchema = chargeActivityDataSchema.extend({ stripeInvoiceId: z.string() });
export const paymentFailedDataSchema = paymentSettledDataSchema.extend({
    failureCode: z.string().nullable(),
    attemptCount: z.number().int(),
});
export const paymentActionRequiredDataSchema = z.object({
    chargeUuid: z.uuid(),
    stripeInvoiceId: z.string(),
    recoveryUrl: z.string().nullable(),
});
// Raised either when a collection fails on a mandate that no longer exists, or when the partner revokes it in their
// TWINT app, which names no charge at all.
export const paymentMethodUnusableDataSchema = z.object({
    reason: z.string(),
    chargeUuid: z.uuid().nullable(),
    stripeInvoiceId: z.string().nullable(),
});
// The subscription rather than the invoice, because what is reported is the subscription starting to fail.
export const paymentRecoveryStartedDataSchema = z.object({
    paymentMethod: subscriptionPaymentMethodSchema,
    stripeSubscriptionId: z.string(),
});
export const paymentRecoveryExhaustedDataSchema = paymentFailedDataSchema;
export const refundIssuedDataSchema = chargeActivityDataSchema.extend({
    correctionUuid: z.uuid(),
    stripeRefundId: z.string(),
});
export const creditNoteIssuedDataSchema = chargeActivityDataSchema.extend({
    creditNoteNumber: z.string().nullable(),
});
// A status prefixed with `warning` means the dispute is still open and no money has been debited yet.
export const disputeOpenedDataSchema = chargeActivityDataSchema.extend({
    stripeDisputeId: z.string(),
    reason: z.string(),
    status: z.string(),
    evidenceDueBy: z.iso.datetime().nullable(),
});
export const disputeClosedDataSchema = chargeActivityDataSchema.extend({
    stripeDisputeId: z.string(),
    outcome: z.string(),
});
export const invoiceClosedDataSchema = chargeActivityDataSchema.extend({ stripeInvoiceId: z.string() });
export const bexioInvoiceSentDataSchema = z.object({
    chargeUuid: z.uuid(),
    bexioInvoiceNumber: z.string().nullable(),
});
// totalPaid is the running total, so an instalment reads against what the charge has received so far.
export const paymentReceivedDataSchema = chargeActivityDataSchema.extend({ totalPaid: z.string() });
// The invoice path's counterpart to PaymentRecoveryStarted, and deliberately not that type: we run no retry ladder
// here, so no recovery of ours begins and none is ever exhausted. What happened is that a due date passed unpaid and
// a sweep noticed. amount is what is still outstanding on the charge rather than what the charge is worth.
export const bexioInvoiceOverdueDataSchema = chargeActivityDataSchema.extend({
    dueAt: z.iso.datetime(),
    // Null while a term is owed but no document has been raised for it yet.
    bexioInvoiceNumber: z.string().nullable(),
});
export const invoiceGenerationFailedDataSchema = chargeActivityDataSchema.extend({ reason: z.string() });
export const subscriptionChangePayloadSchemas = {
    [SubscriptionChangeType.Activated]: activatedDataSchema,
    [SubscriptionChangeType.Prolongation]: prolongationDataSchema,
    [SubscriptionChangeType.PlanChangeScheduled]: planChangeDataSchema,
    [SubscriptionChangeType.PlanChanged]: planChangeDataSchema,
    [SubscriptionChangeType.PlanChangeRevoked]: revokedChangeDataSchema,
    [SubscriptionChangeType.SalesAssigneeChange]: assigneeChangeDataSchema,
    [SubscriptionChangeType.TelesalesAssigneeChange]: assigneeChangeDataSchema,
    [SubscriptionChangeType.CancellationScheduled]: cancellationScheduledDataSchema,
    [SubscriptionChangeType.CancellationRevoked]: revokedChangeDataSchema,
    [SubscriptionChangeType.Terminated]: terminatedDataSchema,
    [SubscriptionChangeType.PaymentMethodChanged]: paymentMethodChangedDataSchema,
    [SubscriptionChangeType.BillingDetailsChanged]: billingDetailsChangedDataSchema,
    [SubscriptionChangeType.TermsAccepted]: termsAcceptedDataSchema,
    [SubscriptionChangeType.ValidationRejected]: validationRejectedDataSchema,
    [SubscriptionChangeType.FreeTimeScheduled]: freeTimeScheduledDataSchema,
    [SubscriptionChangeType.FreeTimeRevoked]: revokedChangeDataSchema,
    [SubscriptionChangeType.FreeTimeGranted]: freeTimeGrantedDataSchema,
    [SubscriptionChangeType.ScheduleChanged]: scheduleChangedDataSchema,
    [SubscriptionChangeType.CreditsApplied]: creditsAppliedDataSchema,
    [SubscriptionChangeType.Migration]: migrationDataSchema,
    [SubscriptionChangeType.CheckoutLinkSent]: checkoutLinkSentDataSchema,
    [SubscriptionChangeType.CheckoutReminderSent]: checkoutReminderSentDataSchema,
    [SubscriptionChangeType.SaleProvenanceChanged]: saleProvenanceChangedDataSchema,
    [SubscriptionChangeType.ProlongationReminderSent]: prolongationReminderSentDataSchema,
    [SubscriptionChangeType.ValidationConfirmationSent]: validationConfirmationSentDataSchema,
    [SubscriptionChangeType.PaymentMethodValidated]: paymentMethodValidatedDataSchema,
    [SubscriptionChangeType.PaymentSuccessful]: paymentSettledDataSchema,
    [SubscriptionChangeType.PaymentFailed]: paymentFailedDataSchema,
    [SubscriptionChangeType.PaymentRecoveryAttempt]: paymentFailedDataSchema,
    [SubscriptionChangeType.PaymentRecoveryStarted]: paymentRecoveryStartedDataSchema,
    [SubscriptionChangeType.PaymentRecoveryExhausted]: paymentRecoveryExhaustedDataSchema,
    [SubscriptionChangeType.PaymentActionRequired]: paymentActionRequiredDataSchema,
    [SubscriptionChangeType.PaymentMethodUnusable]: paymentMethodUnusableDataSchema,
    [SubscriptionChangeType.RefundIssued]: refundIssuedDataSchema,
    [SubscriptionChangeType.CreditNoteIssued]: creditNoteIssuedDataSchema,
    [SubscriptionChangeType.DisputeOpened]: disputeOpenedDataSchema,
    [SubscriptionChangeType.DisputeClosed]: disputeClosedDataSchema,
    [SubscriptionChangeType.InvoiceVoided]: invoiceClosedDataSchema,
    [SubscriptionChangeType.InvoiceUncollectible]: invoiceClosedDataSchema,
    [SubscriptionChangeType.BexioInvoiceSent]: bexioInvoiceSentDataSchema,
    [SubscriptionChangeType.PaymentReceived]: paymentReceivedDataSchema,
    [SubscriptionChangeType.BexioInvoiceOverdue]: bexioInvoiceOverdueDataSchema,
    [SubscriptionChangeType.InvoiceGenerationFailed]: invoiceGenerationFailedDataSchema,
};
export const subscriptionChangeCategorySchema = z.enum(['Lifecycle', 'Plan', 'FreeTime', 'Payment', 'Admin']);
export const SubscriptionChangeCategory = subscriptionChangeCategorySchema.enum;
// Filtering by one of thirty-two types is unusable, so the history filter offers these instead.
export const subscriptionChangeCategories = {
    [SubscriptionChangeType.Activated]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.Prolongation]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.PlanChangeScheduled]: SubscriptionChangeCategory.Plan,
    [SubscriptionChangeType.PlanChanged]: SubscriptionChangeCategory.Plan,
    [SubscriptionChangeType.PlanChangeRevoked]: SubscriptionChangeCategory.Plan,
    [SubscriptionChangeType.SalesAssigneeChange]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.TelesalesAssigneeChange]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.CancellationScheduled]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.CancellationRevoked]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.Terminated]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.PaymentMethodChanged]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.BillingDetailsChanged]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.TermsAccepted]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.ValidationRejected]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.FreeTimeScheduled]: SubscriptionChangeCategory.FreeTime,
    [SubscriptionChangeType.FreeTimeRevoked]: SubscriptionChangeCategory.FreeTime,
    [SubscriptionChangeType.FreeTimeGranted]: SubscriptionChangeCategory.FreeTime,
    [SubscriptionChangeType.ScheduleChanged]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.CreditsApplied]: SubscriptionChangeCategory.FreeTime,
    [SubscriptionChangeType.Migration]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.SaleProvenanceChanged]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.CheckoutLinkSent]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.CheckoutReminderSent]: SubscriptionChangeCategory.Admin,
    [SubscriptionChangeType.ProlongationReminderSent]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.ValidationConfirmationSent]: SubscriptionChangeCategory.Lifecycle,
    [SubscriptionChangeType.PaymentMethodValidated]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentSuccessful]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentFailed]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentRecoveryAttempt]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentRecoveryStarted]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentRecoveryExhausted]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentActionRequired]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentMethodUnusable]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.RefundIssued]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.CreditNoteIssued]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.DisputeOpened]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.DisputeClosed]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.InvoiceVoided]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.InvoiceUncollectible]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.BexioInvoiceSent]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.PaymentReceived]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.BexioInvoiceOverdue]: SubscriptionChangeCategory.Payment,
    [SubscriptionChangeType.InvoiceGenerationFailed]: SubscriptionChangeCategory.Payment,
};
export const subscriptionChangeTypesOf = (category) => subscriptionChangeTypeSchema.options.filter((type) => subscriptionChangeCategories[type] === category);
